'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { Fragment, type ReactNode, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Checkbox, Input } from '@nwwp/ui-shadcn'
import { useVeritonic } from '@nwwp/veritonic'

import { PhoneDisclosure } from './PhoneDisclosure'
import { ButtonCTA } from '../../controls/ButtonCTA'
import { NumberInput } from '../../controls/NumberInput'
import { RangeSelectInput } from '../../controls/RangeSelectInput'
import { buildLeadMagnetSchema, type LeadMagnetSchema } from '../../lib/lead-magnet-schema'
import {
  INVESTABLE_ASSETS_OPTIONS,
  isLeadMagnetIncomeQualified,
  isLeadMagnetQualified,
  saveLeadMagnetIncomeQualification,
  saveLeadMagnetQualification,
} from '../../utils/lead-magnet-qualification'
import { setLeadMagnetUnlockCookie } from '../../utils/lead-magnet-unlock'
import { saveSchedulePrefill } from '../../utils/schedule-prefill'
import { getTrackingParams } from '../../utils/tracking'
import { DEFAULT_FORM_FIELDS, type LeadMagnetFormField, type LeadMagnetPage } from '../config'

export type CreateLeadMagnetLeadFn = (data: LeadMagnetSchema, tracking: Record<string, string>) => Promise<boolean>

type Props = {
  magnet: LeadMagnetPage
  createLead: CreateLeadMagnetLeadFn
  /** Called after a successful reveal submit (cookie already set). */
  onSuccess?: () => void
  /** Fire once on first form field interaction (Veritonic flow start). */
  onFlowStart?: () => void
  /**
   * Values the host page already collected (e.g. calculator inputs). Submitted with the lead instead of
   * re-asking — only pass values for fields not listed in `magnet.formFields`.
   */
  knownValues?: Partial<
    Pick<LeadMagnetSchema, 'age' | 'household_income' | 'investable_assets_range' | 'current_retirement'>
  >
}

/** Default on-page order; magnets opt into their own `formFields` order via `orderFieldsAsListed`. */
const FIELD_RENDER_ORDER: LeadMagnetFormField[] = [
  'household_income',
  'investable_assets_range',
  'age',
  'first_name',
  'last_name',
  'email',
  'schedule_call_interest',
  'phone',
]

export function LeadCaptureForm({ magnet, createLead, onSuccess, onFlowStart, knownValues }: Props) {
  const router = useRouter()
  const { trackFlowComplete } = useVeritonic()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const flowStartedRef = useRef(false)
  const fields = magnet.formFields ?? DEFAULT_FORM_FIELDS
  const phoneRequired = !!magnet.phoneRequired
  const req = magnet.hideRequiredAsterisks ? '' : ' *'
  const disclosurePlacement = magnet.disclosurePlacement ?? 'before-submit'
  const schema = useMemo(() => buildLeadMagnetSchema(fields, { phoneRequired }), [fields, phoneRequired])

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      age: undefined,
      household_income: '',
      schedule_call_interest: fields.includes('schedule_call_interest') ? true : undefined,
      investable_assets_range: undefined,
      ...knownValues,
    },
  })

  const phone = useWatch({ control, name: 'phone' })

  const markFlowStarted = () => {
    if (flowStartedRef.current || !onFlowStart) return
    flowStartedRef.current = true
    onFlowStart()
  }

  const onSubmit = async (formData: LeadMagnetSchema) => {
    const data = { ...formData, ...knownValues }
    try {
      setIsSubmitting(true)
      setSubmitError(null)
      const success = await createLead(data, { ...getTrackingParams(), lead_magnet_slug: magnet.slug })
      if (!success) {
        setSubmitError('Something went wrong. Please try again.')
        return
      }
      try {
        trackFlowComplete()
      } catch (e) {
        console.warn('trackFlowComplete failed', { error: e })
      }
      saveSchedulePrefill({
        first_name: data.first_name ?? '',
        last_name: data.last_name ?? '',
        email: data.email,
        phone: data.phone,
      })
      saveLeadMagnetQualification(
        isLeadMagnetQualified(data.household_income || undefined, data.investable_assets_range)
      )
      // Shared session key — only write when this submit actually collected HHI,
      // so other magnets don't clear Coast sticky for visitors who already qualified.
      if (fields.includes('household_income') || knownValues?.household_income !== undefined) {
        saveLeadMagnetIncomeQualification(isLeadMagnetIncomeQualified(data.household_income || undefined))
      }
      if (magnet.ctaType === 'redirect' || magnet.ctaType === 'reveal') {
        setLeadMagnetUnlockCookie(magnet.slug)
        if (magnet.ctaType === 'reveal') onSuccess?.()
        else router.push(magnet.ctaUrl)
      } else if (magnet.ctaType === 'schedule') {
        router.push(`/lp/${magnet.slug}/get-started`)
      } else if (magnet.ctaType === 'email') setSubmitSuccess(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return <p className="text-center text-body">Thanks! We&apos;ll be in touch shortly.</p>
  }

  const phoneDisclosure = fields.includes('phone') && (phoneRequired || !!phone) && (
    <PhoneDisclosure
      ctaText={magnet.ctaText}
      className={magnet.disclosureClassName}
      ctaPlacement={disclosurePlacement === 'after-submit' ? 'above' : 'below'}
    >
      {magnet.phoneDisclosure}
    </PhoneDisclosure>
  )

  const fieldNodes: Record<LeadMagnetFormField, ReactNode> = {
    household_income: (
      <Controller
        name="household_income"
        control={control}
        render={({ field }) => (
          <NumberInput
            name={field.name}
            ref={field.ref}
            value={(field.value as string | number | undefined) ?? ''}
            onBlur={field.onBlur}
            onChange={(e) => field.onChange(e.target.value)}
            placeholder={`Household Income${req}`}
            prefix="$"
            error={errors.household_income?.message}
          />
        )}
      />
    ),
    investable_assets_range: (
      <Controller
        name="investable_assets_range"
        control={control}
        render={({ field }) => (
          <RangeSelectInput
            id="investable_assets_range"
            label={`Investable Assets${req}`}
            name={field.name}
            value={field.value ?? ''}
            onChange={(value) => field.onChange(value)}
            options={INVESTABLE_ASSETS_OPTIONS}
            error={errors.investable_assets_range?.message}
            onBlur={field.onBlur}
          />
        )}
      />
    ),
    age: (
      <Input
        {...register('age', { setValueAs: (v) => (v === '' ? undefined : v) })}
        type="number"
        placeholder={`Age${req}`}
        error={errors.age?.message ?? null}
      />
    ),
    first_name: (
      <Input {...register('first_name')} placeholder={`First Name${req}`} error={errors.first_name?.message ?? null} />
    ),
    last_name: (
      <Input {...register('last_name')} placeholder={`Last Name${req}`} error={errors.last_name?.message ?? null} />
    ),
    email: (
      <Input {...register('email')} type="email" placeholder={`Email${req}`} error={errors.email?.message ?? null} />
    ),
    schedule_call_interest: (
      <Controller
        name="schedule_call_interest"
        control={control}
        render={({ field }) => (
          <div className="flex items-start gap-2">
            <Checkbox
              id="schedule_call_interest"
              checked={!!field.value}
              onCheckedChange={field.onChange}
              className="mt-0.5"
            />
            <label htmlFor="schedule_call_interest" className="cursor-pointer text-body">
              I am interested in scheduling a call with a financial advisor
            </label>
          </div>
        )}
      />
    ),
    phone: (
      <Controller
        name="phone"
        control={control}
        render={({ field }) => (
          <NumberInput
            name={field.name}
            ref={field.ref}
            value={(field.value as string | number | undefined) ?? ''}
            onBlur={field.onBlur}
            onChange={(e) => field.onChange(e.target.value)}
            mask="phone"
            placeholder={phoneRequired ? `Phone${req}` : 'Phone'}
            error={errors.phone?.message}
          />
        )}
      />
    ),
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onFocusCapture={markFlowStarted} className="flex w-full flex-col gap-6">
      {(magnet.orderFieldsAsListed ? fields : FIELD_RENDER_ORDER)
        .filter((field) => fields.includes(field))
        .map((field) => (
          <Fragment key={field}>{fieldNodes[field]}</Fragment>
        ))}

      {disclosurePlacement === 'before-submit' && phoneDisclosure}

      <ButtonCTA type="submit" variant={magnet.submitVariant} disabled={!isValid || isSubmitting}>
        {isSubmitting && (
          <span
            className="size-4 animate-spin rounded-full border-2 border-black/30 border-t-black"
            aria-hidden="true"
          />
        )}
        {isSubmitting ? 'Submitting...' : magnet.ctaText}
      </ButtonCTA>

      {submitError && <p className="text-body-sm text-red">{submitError}</p>}

      {disclosurePlacement === 'after-submit' && phoneDisclosure}
    </form>
  )
}
